import React, { useMemo, useState } from 'react';
import { Box, FormControl, MenuItem, Select, Typography } from '@mui/material';
import Pagination from '../../../../components/atoms/Pagination';
import { dashboardTokens } from '../../../Dashboard/Components/dashboardCardStyles';
import { leadStatusMeta } from '../../components/leadsMeta';
import { LeadsListTable } from '../../components/LeadsListTable';
import { MarketingLead, MarketingLeadStatus } from '../../../../services/marketingLeads';

const STATUS_OPTIONS: MarketingLeadStatus[] = [
  'NEW',
  'CONTACTED',
  'QUALIFIED',
  'CONVERTED',
  'LOST',
];

const selectSx = {
  minWidth: 145,
  color: '#e2e8f0',
  bgcolor: '#1c1c1c',
  '.MuiOutlinedInput-notchedOutline': { borderColor: dashboardTokens.border },
  '.MuiSvgIcon-root': { color: '#94a3b8' },
};

interface MetaLeadsTableProps {
  leads: MarketingLead[];
  loading: boolean;
  platformLabel?: string;
  /**
   * How many leads the server holds for the selected range — which is not
   * `leads.length` once the range holds more than one fetch page.
   */
  total?: number;
  /** The range these rows cover, so the heading can't overstate them. */
  rangeLabel?: string;
}

export const MetaLeadsTable: React.FC<MetaLeadsTableProps> = ({
  leads,
  loading,
  platformLabel = 'Meta Ads',
  total,
  rangeLabel,
}) => {
  const [campaignName, setCampaignName] = useState('');
  const [status, setStatus] = useState<MarketingLeadStatus | ''>('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const campaignOptions = useMemo(() => {
    const names = new Set<string>();
    leads.forEach((lead) => names.add(lead.campaignName || 'Uncategorized'));
    return Array.from(names).sort();
  }, [leads]);

  const filtered = useMemo(
    () =>
      leads.filter((lead) => {
        const name = lead.campaignName || 'Uncategorized';
        if (campaignName && name !== campaignName) return false;
        if (status && lead.status !== status) return false;
        return true;
      }),
    [leads, campaignName, status],
  );

  const pageRows = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box
      sx={{
        bgcolor: dashboardTokens.cardBg,
        border: `1px solid ${dashboardTokens.border}`,
        borderRadius: dashboardTokens.cardRadius,
        p: 2.5,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          mb: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 17, fontWeight: 700, color: '#fff' }}>
            {platformLabel} Leads
            {typeof total === 'number' && (
              <Box component="span" sx={{ color: dashboardTokens.textMuted, fontWeight: 500 }}>
                {' '}
                · {total}
              </Box>
            )}
          </Typography>
          {rangeLabel && (
            <Typography sx={{ fontSize: 12, color: dashboardTokens.textSecondary, mt: 0.25 }}>
              {rangeLabel}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
          <FormControl size="small">
            <Select
              displayEmpty
              value={campaignName}
              onChange={(e) => {
                setCampaignName(e.target.value);
                setPage(0);
              }}
              sx={selectSx}
            >
              <MenuItem value="">Campaign</MenuItem>
              {campaignOptions.map((name) => (
                <MenuItem key={name} value={name}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small">
            <Select
              displayEmpty
              value={status}
              onChange={(e) => {
                setStatus(e.target.value as MarketingLeadStatus | '');
                setPage(0);
              }}
              sx={selectSx}
            >
              <MenuItem value="">Status</MenuItem>
              {STATUS_OPTIONS.map((s) => (
                <MenuItem key={s} value={s}>
                  {leadStatusMeta[s].label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Box sx={{ overflowX: 'auto' }}>
        <LeadsListTable
          leads={pageRows}
          isLoading={loading}
          noDataText={`No ${platformLabel} leads for the selected filters yet.`}
        />
      </Box>

      {filtered.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
          <Pagination
            count={filtered.length}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={(_: unknown, newPage: number) => setPage(newPage)}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
          />
        </Box>
      )}
    </Box>
  );
};
